import { toast } from "sonner";
import { Check, X, Trash2, Star, RotateCcw } from "lucide-react";
import { api, errorMessage } from "@/lib/adminApi";

const BTN = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-200";

export default function ReviewList({ items, onChanged }) {
    const act = async (fn, msg) => {
        try {
            await fn();
            toast.success(msg);
            onChanged();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const setStatus = (id, status) => act(() => api.patch(`/admin/testimonials/${id}`, { status }), `Review ${status}`);
    const remove = (id) => act(() => api.delete(`/admin/testimonials/${id}`), "Review deleted");

    if (items.length === 0) {
        return (
            <p className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="review-list-empty">
                Nothing here yet.
            </p>
        );
    }

    return (
        <ul className="space-y-3" data-testid="review-list">
            {items.map((r) => (
                <li key={r.id} className="rounded-2xl border border-border bg-surface p-5" data-testid={`review-item-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="font-semibold">{r.author}</p>
                                <span className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Star key={i} size={12} className={i <= r.rating ? "fill-primary text-primary" : "text-border"} />
                                    ))}
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    {r.source}{r.review_date ? ` · ${r.review_date}` : ""}
                                </span>
                            </div>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                            {r.status !== "approved" && (
                                <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-btn-${r.id}`} className={`${BTN} border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground`}>
                                    <Check size={13} /> Approve
                                </button>
                            )}
                            {r.status !== "rejected" && (
                                <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-btn-${r.id}`} className={`${BTN} border-border text-muted-foreground hover:border-foreground hover:text-foreground`}>
                                    <X size={13} /> Reject
                                </button>
                            )}
                            {r.status !== "pending" && (
                                <button onClick={() => setStatus(r.id, "pending")} data-testid={`pending-btn-${r.id}`} className={`${BTN} border-border text-muted-foreground hover:border-foreground hover:text-foreground`}>
                                    <RotateCcw size={13} /> Pending
                                </button>
                            )}
                            <button onClick={() => remove(r.id)} data-testid={`delete-btn-${r.id}`} aria-label="Delete review" className={`${BTN} border-border text-muted-foreground hover:border-destructive hover:text-destructive`}>
                                <Trash2 size={13} />
                            </button>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}
